import { getDefaultStaffImage } from "@/utils/avatar.utils";
import { RoleEnum } from "@/types/user.definition";
import { getDateOfWeek } from "@/utils/date.utils";
import { computeSlotStats, slotStatsRange } from "@/utils/slot-stats.utils";
import {
  AlertTriangle,
  Button, Calendar,
  Clock,
  Mail,
  Phone,
  RefreshCw,
  Stethoscope,
  User,
  formatPhoneNumberForDisplay
} from "@ews-admin/global-design-system";
import { formatDistanceToNow, parseISO } from "date-fns";
import { formatInTimeZone } from "date-fns-tz";
import { enUS, fr } from "date-fns/locale";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  AvailabilityForm,
  AvailabilityFormData,
} from "../../components/molecules/AvailabilityForm/AvailabilityForm";
import { ConfirmationModal } from "../../components/molecules/ConfirmationModal/ConfirmationModal";
import { DeleteByDateRangeModal } from "../../components/molecules/DeleteByDateRangeModal/DeleteByDateRangeModal";
import { DoctorAvailabilitySelector } from "../../components/molecules/DoctorAvailabilitySelector/DoctorAvailabilitySelector";
import { DoctorSearchAutocomplete } from "../../components/molecules/DoctorSearchAutocomplete/DoctorSearchAutocomplete";
import { LicenceModal } from "../../components/molecules/LicenceModal/LicenceModal";
import { PageHeader } from "../../components/molecules/PageHeader/PageHeader";
import { Specialties } from "../../components/molecules/Specialties";
import { StatCard } from "../../components/molecules/StatCard/StatCard";
import useFacilityUser from "../../hooks/useFacilityUser";
import { useHasLicence } from "../../hooks/useHasLicence";
import { doctorApi } from "../../services/doctor.api";
import { Doctor, MIN_BOOKING_HOURS_AHEAD, TimeSlot } from "../../types/doctor.definition";

export const ManageAvailability: React.FC = () => {
  const { t, i18n } = useTranslation();
  // Dates in the interface language (MED-1687: they showed in English on a French screen)
  const dateLocale = i18n.language?.startsWith("en") ? enUS : fr;
  const facilityUser = useFacilityUser();
  const facilityId = Number(facilityUser.healthcareFacility?.id);
  const hasLicence = useHasLicence();
  // MED-1687: a secretary cannot open the licence page, so instead of the "Licence requise"
  // modal she sees why the availability section is unavailable.
  const showLicenceWarning = facilityUser?.roleName === RoleEnum.SECRETARY && !hasLicence;

  const [selectedDoctorId, setSelectedDoctorId] = useState<string>("");
  const [selectedDoctor, setSelectedDoctor] = useState<Doctor | null>(null);
  const [doctors, setDoctors] = useState<Doctor[]>([]);
  const [isLoadingDoctors, setIsLoadingDoctors] = useState(false);
  const availabilitySelectorRef = useRef<{ refresh: () => void } | null>(null);
  const [showAvailabilityForm, setShowAvailabilityForm] = useState(false);
  const [showLicenceModal, setShowLicenceModal] = useState(false);
  const [showDeleteRangeModal, setShowDeleteRangeModal] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [timeSlotToDelete, setTimeSlotToDelete] = useState<TimeSlot | null>(
    null,
  );
  const [slotStats, setSlotStats] = useState<{ available: number; upcoming: number } | null>(
    null,
  );

  // Fetch doctors in facility
  const fetchDoctors = useCallback(async () => {
    if (!facilityId) return;

    setIsLoadingDoctors(true);

    try {
      // Use search API to get doctors in facility
      const { searchApi } = await import("../../services/search.api");
      const { defaultSearchRequest, Order } =
        await import("../../types/search.definition");

      const response = await searchApi.searchDoctorsInFacility(
        {
          ...defaultSearchRequest,
          paginationSettings: {
            limit: 100,
            offset: 0,
          },
          sortBy: {
            property: "user.firstName",
            order: Order.ASC,
          },
          query: {
            keyword: "",
          },
        },
        facilityId,
      );

      setDoctors(response.data || []);
    } catch (err) {
      console.error("Error fetching doctors:", err);
      toast.error(
        t(
          "availability.loadDoctorsError",
          "Failed to load doctors. Please try again.",
        ),
      );
    } finally {
      setIsLoadingDoctors(false);
    }
  }, [facilityId, t]);

  useEffect(() => {
    fetchDoctors();
  }, [fetchDoctors]);

  const handleDoctorSelect = useCallback(
    (doctorId: string, doctor?: Doctor) => {
      setSelectedDoctorId(doctorId);
      if (doctor) {
        setSelectedDoctor(doctor);
      } else {
        if (doctorId) {
          doctorApi
            .getDoctorById(doctorId)
            .then((data) => setSelectedDoctor(data))
            .catch((err) => {
              console.error("Error fetching doctor data:", err);
              toast.error(
                t(
                  "availability.loadDoctorError",
                  "Failed to load doctor details.",
                ),
              );
            });
        }
      }
    },
    [t],
  );

  // MED-1687: the "Créneaux Disponibles" / "Créneaux à Venir" counters, next 7 days, this facility
  const fetchSlotStats = useCallback(async () => {
    if (!selectedDoctorId || !facilityId) {
      setSlotStats(null);
      return;
    }
    try {
      const { startDate, endDate } = slotStatsRange();
      const slots = await doctorApi.getDoctorTimeSlotsByDateRange(
        selectedDoctorId,
        startDate,
        endDate,
      );
      setSlotStats(computeSlotStats(slots || [], facilityId));
    } catch (err) {
      console.error("Error fetching slot statistics:", err);
      setSlotStats(null);
    }
  }, [selectedDoctorId, facilityId]);

  useEffect(() => {
    fetchSlotStats();
  }, [fetchSlotStats]);

  const handleRefresh = useCallback(() => {
    if (availabilitySelectorRef.current) {
      availabilitySelectorRef.current.refresh();
    }
    fetchSlotStats();
  }, [fetchSlotStats]);

  const handleDeleteAvailability = (slot: TimeSlot) => {
    setTimeSlotToDelete(slot);
    setShowDeleteConfirmation(true);
  };

  const confirmDeleteAvailability = async () => {
    if (timeSlotToDelete?.id && selectedDoctorId) {
      try {
        await doctorApi.deleteDoctorTimeSlot(
          selectedDoctorId,
          timeSlotToDelete.id,
        );
        toast.success(
          t("availability.deleteSuccess", "Availability deleted successfully."),
        );
        handleRefresh();
      } catch (err) {
        console.error("Failed to delete availability:", err);
        toast.error(
          t("availability.deleteError", "Failed to delete availability."),
        );
      } finally {
        setShowDeleteConfirmation(false);
        setTimeSlotToDelete(null);
      }
    }
  };

  const handleAvailabilitySubmit = async (data: AvailabilityFormData) => {
    if (!data.isRecurring) {
      const dateOfWeek = getDateOfWeek(data.startDate);
      if (dateOfWeek) {
        data.daysOfWeek = [dateOfWeek];
      }
    }
    if (!selectedDoctorId) {
      toast.error(
        t("availability.noDoctorSelected", "Please select a doctor first."),
      );
      return;
    }

    try {
      // Convert days of week to the format expected by the API
      const payload = {
        startDate: data.startDate,
        endDate: data.endDate,
        startTime: data.startTime,
        endTime: data.endTime,
        daysOfWeek: data.daysOfWeek,
        isRecurring: data.isRecurring,
        slotDurationMinutes: data.slotDurationMinutes || 30,
      };

      await doctorApi.createDoctorTimeSlots(selectedDoctorId, payload);
      toast.success(
        t("availability.createSuccess", "Availability created successfully."),
      );
      setShowAvailabilityForm(false);
      handleRefresh();
    } catch (err) {
      console.error("Failed to create availability:", err);
      toast.error(
        t("availability.createError", "Failed to create availability."),
      );
      throw err;
    }
  };

  // Calculate statistics
  const stats = useMemo(() => {
    const totalDoctors = doctors.length;
    return {
      totalDoctors,
      availableSlots: slotStats ? slotStats.available : "-",
      upcomingSlots: slotStats ? slotStats.upcoming : "-",
    };
  }, [doctors.length, slotStats]);

  if (!facilityId) {
    return (
      <div className="p-6">
        <div className="text-center text-neutral-600">
          No facility associated with your account.
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6 min-h-screen bg-neutral-50">
      <PageHeader
        title={t("availability.title", "Gérer la Disponibilité des Médecins")}
        description={t(
          "availability.description",
          "Consultez et gérez les horaires de disponibilité des médecins de votre établissement",
        )}
        icon={Calendar}
        actions={
          <div className="flex items-center space-x-3">
            {selectedDoctorId && (
              <Button variant="outline" size="sm" onClick={handleRefresh}>
                <RefreshCw className="mr-2 w-4 h-4" />
                {t("availability.refresh", "Actualiser")}
              </Button>
            )}
          </div>
        }
      />

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <StatCard
          title={t("availability.totalDoctors", "Médecins Totaux")}
          value={stats.totalDoctors}
          icon={Stethoscope}
          description={t("availability.inFacility", "dans l'établissement")}
        />
        <StatCard
          title={t("availability.availableSlots", "Créneaux Disponibles")}
          value={selectedDoctorId ? stats.availableSlots : "-"}
          icon={Clock}
          description={
            selectedDoctorId
              ? t("availability.next7Days", "7 prochains jours")
              : t("availability.selectDoctor", "Sélectionnez un médecin")
          }
        />
        <StatCard
          title={t("availability.upcomingSlots", "Créneaux à Venir")}
          value={selectedDoctorId ? stats.upcomingSlots : "-"}
          icon={Calendar}
          description={
            selectedDoctorId
              ? t("availability.next7DaysAllStatuses")
              : t("availability.selectDoctor", "Sélectionnez un médecin")
          }
        />
      </div>

      {/* Doctor Selection and List */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Doctor Selection Sidebar */}
        <div className="space-y-4 lg:col-span-1">
          {/* Search */}
          <div className="p-6 bg-white rounded-xl border shadow-sm border-neutral-200">
            <div className="flex items-center mb-4 space-x-2">
              <Stethoscope className="w-5 h-5 text-primary-600" />
              <h2 className="text-lg font-semibold text-neutral-900">
                {t("availability.selectDoctor", "Sélectionner un Médecin")}
              </h2>
            </div>

            <div className="mb-4">
              <DoctorSearchAutocomplete
                onSelect={handleDoctorSelect}
                selectedDoctorId={selectedDoctorId}
                disabled={isLoadingDoctors}
              />
            </div>

            {isLoadingDoctors && (
              <div className="py-4 text-sm text-center text-neutral-600">
                {t("availability.loadingDoctors", "Chargement des médecins...")}
              </div>
            )}
          </div>

          {/* Doctors List */}
          <div className="p-6 bg-white rounded-xl border shadow-sm border-neutral-200">
            <h3 className="mb-4 text-sm font-semibold text-neutral-700">
              {t("availability.allDoctors", "Tous les Médecins")} (
              {doctors.length})
            </h3>
            <div className="space-y-3 max-h-[600px] overflow-y-auto">
              {doctors.map((doctor) => {
                const isSelected = selectedDoctorId === String(doctor.id);
                let nextAvail: string | null = null;
                if (doctor.nextAvailability) {
                  try {
                    const nextAvailDate = parseISO(doctor.nextAvailability);
                    if (!isNaN(nextAvailDate.getTime())) {
                      nextAvail = formatDistanceToNow(nextAvailDate, {
                        addSuffix: true,
                        locale: dateLocale,
                      });
                    }
                  } catch (err) {
                    console.error("Error parsing nextAvailability:", err);
                  }
                }

                return (
                  <button
                    key={doctor.id}
                    onClick={() =>
                      handleDoctorSelect(String(doctor.id), doctor)
                    }
                    className={`w-full p-4 text-left rounded-lg border transition-all duration-200 ${isSelected
                        ? "shadow-md bg-primary-50 border-primary-300"
                        : "bg-neutral-50 border-neutral-200 hover:bg-neutral-100 hover:border-neutral-300"
                      }`}
                  >
                    <div className="flex items-start space-x-3">
                      <div className="flex-shrink-0">
                        <img
                          src={
                            doctor.photoUrl ||
                            getDefaultStaffImage(RoleEnum.DOCTOR, doctor.civility)
                          }
                          alt={`${doctor.firstName} ${doctor.lastName}`}
                          className="object-cover w-12 h-12 rounded-lg border-2 border-neutral-200"
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex justify-between items-center">
                          <h4 className="font-semibold text-neutral-900">
                            Dr. {doctor.firstName} {doctor.lastName}
                          </h4>
                          {isSelected && (
                            <div className="flex-shrink-0 w-2 h-2 rounded-full bg-primary-500"></div>
                          )}
                        </div>

                        {nextAvail && (
                          <div className="flex items-center mt-2 text-xs text-neutral-500">
                            <Clock className="mr-1 w-3 h-3" />
                            <span>
                              {t(
                                "availability.nextAvailable",
                                "Prochaine disponibilité",
                              )}
                              : {nextAvail}
                            </span>
                          </div>
                        )}
                        {doctor.email && (
                          <div className="flex items-center mt-1 text-xs text-neutral-500">
                            <Mail className="mr-1 w-3 h-3" />
                            <span className="truncate">{doctor.email}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>

            {!isLoadingDoctors && doctors.length === 0 && (
              <div className="py-8 text-center">
                <User className="mx-auto mb-3 w-12 h-12 text-neutral-400" />
                <p className="text-sm text-neutral-600">
                  {t(
                    "availability.noDoctors",
                    "Aucun médecin trouvé dans votre établissement.",
                  )}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Availability Calendar */}
        <div className="lg:col-span-2">
          {showLicenceWarning ? (
            <div
              role="alert"
              className="p-8 rounded-xl border bg-warning-50 border-warning-200"
            >
              <div className="flex items-start space-x-4">
                <AlertTriangle className="flex-shrink-0 w-6 h-6 text-warning-600" />
                <div>
                  <h3 className="mb-2 text-lg font-semibold text-neutral-900">
                    {t("availability.licenceWarning.title")}
                  </h3>
                  <p className="text-neutral-700">
                    {t("availability.licenceWarning.message")}
                  </p>
                </div>
              </div>
            </div>
          ) : selectedDoctorId && selectedDoctor ? (
            <div className="p-6 bg-white rounded-xl border shadow-sm border-neutral-200">
              <div className="flex flex-col mb-6 space-y-4 sm:flex-row sm:items-center sm:justify-between sm:space-y-0">
                <div className="flex items-center space-x-4">
                  <div className="flex-shrink-0">
                    <img
                      src={
                        selectedDoctor.photoUrl ||
                        getDefaultStaffImage(RoleEnum.DOCTOR, selectedDoctor.civility)
                      }
                      alt={`${selectedDoctor.firstName} ${selectedDoctor.lastName}`}
                      className="object-cover w-16 h-16 rounded-xl border-2 border-primary-200"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.src = getDefaultStaffImage(RoleEnum.DOCTOR, selectedDoctor.civility);
                      }}
                    />
                  </div>
                  <div className="min-w-0">
                    <h2 className="text-xl font-bold text-neutral-900">
                      Dr. {selectedDoctor.firstName} {selectedDoctor.lastName}
                    </h2>
                    {selectedDoctor.specialties &&
                      selectedDoctor.specialties.length > 0 && (
                        <div className="mt-2">
                          <Specialties
                            specialties={selectedDoctor.specialties}
                            maxVisible={3}
                          />
                        </div>
                      )}
                    {/* MED-1687: wraps on a phone (the number was squeezed digit group by digit
                        group), and the phone shows even without an email */}
                    {(selectedDoctor.email || selectedDoctor.phoneNumber) && (
                      <div className="flex flex-wrap gap-x-4 gap-y-1 items-center mt-2 text-sm text-neutral-500">
                        {selectedDoctor.email && (
                          <div className="flex items-center min-w-0">
                            <Mail className="flex-shrink-0 mr-1 w-4 h-4" />
                            <span className="break-all">{selectedDoctor.email}</span>
                          </div>
                        )}
                        {selectedDoctor.phoneNumber && (
                          <div className="flex items-center whitespace-nowrap">
                            <Phone className="mr-1 w-4 h-4" />
                            <span>{formatPhoneNumberForDisplay(selectedDoctor.phoneNumber)}</span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                {selectedDoctor.nextAvailability &&
                  (() => {
                    try {
                      const nextAvailDate = parseISO(
                        selectedDoctor.nextAvailability,
                      );
                      if (isNaN(nextAvailDate.getTime())) {
                        return null;
                      }
                      return (
                        <div className="px-4 py-2 text-sm rounded-lg border bg-primary-50 text-primary-700 border-primary-200">
                          <div className="flex items-center">
                            <Clock className="mr-2 w-4 h-4" />
                            <span>
                              {t(
                                "availability.nextAvailable",
                                "Prochaine disponibilité",
                              )}
                              : {formatInTimeZone(nextAvailDate, "Africa/Dakar", "PPp", { locale: dateLocale })}
                            </span>
                          </div>
                        </div>
                      );
                    } catch (err) {
                      console.error("Error parsing nextAvailability:", err);
                      return null;
                    }
                  })()}
              </div>

              <div className="pt-4 border-t border-neutral-200">
                {selectedDoctorId ? (
                  <DoctorAvailabilitySelector
                    ref={(ref) => {
                      availabilitySelectorRef.current = ref;
                    }}
                    doctorId={selectedDoctorId}
                    doctor={selectedDoctor}
                    onSelectSlot={() => { }} // Not used in management view
                    selectedSlotId={null}
                    onDeleteSlot={handleDeleteAvailability}
                    showDeleteButton={true}
                    ownFacilityId={facilityId}
                    // MED-1687: only this facility's slots, as when booking (MED-1653)
                    hideOtherFacilitySlots
                    // MED-1687: only days and slots at least 4 h ahead are active, as when booking
                    minBookingHoursAhead={MIN_BOOKING_HOURS_AHEAD}
                    hasLicence={hasLicence}
                    onLicenceRestrictedClick={() => setShowLicenceModal(true)}
                    onAddAvailability={handleAvailabilitySubmit}
                    isSubmittingAvailability={false}
                    onDeleteByDateRange={() => setShowDeleteRangeModal(true)}
                  />
                ) : (
                  <div className="p-8 text-center text-neutral-600">
                    {t("availability.loading", "Chargement...")}
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="p-12 text-center bg-gradient-to-br rounded-xl border from-neutral-50 to-neutral-100 border-neutral-200">
              <div className="inline-flex justify-center items-center mb-4 w-20 h-20 rounded-full bg-primary-100">
                <Clock className="w-10 h-10 text-primary-600" />
              </div>
              <h3 className="mb-2 text-xl font-semibold text-neutral-900">
                {t(
                  "availability.noDoctorSelected",
                  "Aucun Médecin Sélectionné",
                )}
              </h3>
              <p className="mx-auto max-w-md text-neutral-600">
                {t(
                  "availability.selectDoctorToManage",
                  "Sélectionnez un médecin dans la liste pour consulter et gérer ses horaires de disponibilité.",
                )}
              </p>
              {doctors.length > 0 && (
                <p className="mt-4 text-sm text-neutral-500">
                  {t("availability.tip", "💡 Astuce")}:{" "}
                  {t(
                    "availability.searchTip",
                    "Utilisez la barre de recherche ou cliquez sur un médecin dans la liste",
                  )}
                </p>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Availability Form Modal */}
      <AvailabilityForm
        isOpen={showAvailabilityForm}
        onClose={() => setShowAvailabilityForm(false)}
        onSubmit={handleAvailabilitySubmit}
        isSubmitting={false}
      />

      {/* Delete by Date Range Modal */}
      <DeleteByDateRangeModal
        isOpen={showDeleteRangeModal}
        onClose={() => setShowDeleteRangeModal(false)}
        doctorId={selectedDoctorId}
        onDeleted={handleRefresh}
      />

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteConfirmation}
        onCancel={() => {
          setShowDeleteConfirmation(false);
          setTimeSlotToDelete(null);
        }}
        onConfirm={confirmDeleteAvailability}
        title={t("availability.confirmDeleteTitle", "Confirm Deletion")}
        message={t(
          "availability.confirmDelete",
          "Are you sure you want to delete this availability slot? This action cannot be undone.",
        )}
        variant="danger"
      />

      <LicenceModal
        isOpen={showLicenceModal}
        onClose={() => setShowLicenceModal(false)}
      />
    </div>
  );
};
